import {
  normalizeVehicleId,
  toMetaContentIds,
  type VehicleIdSource,
} from "~/utils/meta/normalize";
import { trackMetaStandardEvent } from "~/utils/meta/pixel";

type LeadType = "phone" | "message" | "whatsapp" | "form";
type MetaPayload = Record<string, unknown>;
type VehicleFieldKey = "transmission" | "fuel_type" | "body_style";

const viewContentDedupe = new Set<string>();

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);

const normalizeFilterValue = (value: unknown): unknown => {
  if (value === null || typeof value === "undefined") return undefined;

  if (Array.isArray(value)) {
    const list = value
      .map((item) => normalizeFilterValue(item))
      .filter((item) => typeof item !== "undefined");
    return list.length ? list : undefined;
  }

  if (isPlainObject(value)) {
    if (typeof value.name === "string" && value.name.trim()) return value.name.trim();
    if (typeof value.value === "string" && value.value.trim()) return value.value.trim();
    if (typeof value.id === "number" || typeof value.id === "string") return String(value.id);

    const next = normalizeFilters(value);
    return Object.keys(next).length ? next : undefined;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length ? trimmed : undefined;
  }

  if (typeof value === "number" || typeof value === "boolean") return value;

  return undefined;
};

const normalizeFilters = (filters: Record<string, unknown>): Record<string, unknown> =>
  Object.entries(filters).reduce<Record<string, unknown>>((acc, [key, value]) => {
    const normalized = normalizeFilterValue(value);
    if (typeof normalized !== "undefined") {
      acc[key] = normalized;
    }
    return acc;
  }, {});

const toReadableFilterValue = (value: unknown): string => {
  if (Array.isArray(value)) return value.map(toReadableFilterValue).join(", ");
  if (isPlainObject(value)) return Object.values(value).map(toReadableFilterValue).join(", ");
  return String(value);
};

const buildSearchString = (filters: Record<string, unknown>): string =>
  Object.entries(filters)
    .map(([key, value]) => `${key}:${toReadableFilterValue(value)}`)
    .join(" | ");

const stripUnsafeKeys = (payload?: MetaPayload): MetaPayload => {
  if (!payload) return {};
  const {
    content_id: _contentId,
    content_ids: _contentIds,
    content_type: _contentType,
    vehicle: _vehicle,
    ...safe
  } = payload;
  return safe;
};

const hasValue = (value: unknown): boolean => {
  if (value === null || typeof value === "undefined") return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.some(hasValue);
  return true;
};

const readPathValue = (source: unknown, path: string): unknown =>
  path
    .split(".")
    .reduce<unknown>(
      (current, segment) =>
        isPlainObject(current) ? (current as MetaPayload)[segment] : undefined,
      source
    );

const pickValue = (source: unknown, paths: string[]): unknown => {
  for (const path of paths) {
    const value = readPathValue(source, path);
    if (hasValue(value)) return value;
  }
  return undefined;
};

const toStringScalar = (value: unknown): string | undefined => {
  if (value === null || typeof value === "undefined") return undefined;

  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length ? trimmed : undefined;
  }

  if (typeof value === "number" && Number.isFinite(value)) return String(value);

  if (Array.isArray(value)) {
    for (const item of value) {
      const resolved = toStringScalar(item);
      if (resolved) return resolved;
    }
    return undefined;
  }

  if (isPlainObject(value)) {
    const objectValue = value as MetaPayload;
    for (const key of ["name", "label", "value", "id", "code"]) {
      const resolved = toStringScalar(objectValue[key]);
      if (resolved) return resolved;
    }
  }

  return undefined;
};

const splitCompositeString = (value: string): string[] => {
  const trimmed = value.trim();
  if (!trimmed.length) return [];

  const parts = trimmed
    .split(/[_|,]/g)
    .map((item) => item.trim())
    .filter(Boolean);

  return parts.length > 1 ? parts : [trimmed];
};

const toStringList = (value: unknown): string[] => {
  if (value === null || typeof value === "undefined") return [];

  if (Array.isArray(value)) {
    const values = value.flatMap((item) => toStringList(item));
    return Array.from(new Set(values));
  }

  if (typeof value === "string") {
    return splitCompositeString(value);
  }

  const scalar = toStringScalar(value);
  return scalar ? [scalar] : [];
};

const toJoinedString = (value: unknown): string | undefined => {
  const values = toStringList(value);
  return values.length ? values.join(", ") : undefined;
};

const normalizeToken = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const vehicleFieldTranslations: Record<VehicleFieldKey, Record<string, string>> = {
  transmission: {
    automatique: "Automatic",
    automatic: "Automatic",
    manuel: "Manual",
    manual: "Manual",
  },
  fuel_type: {
    essence: "Gasoline",
    gasoline: "Gasoline",
    diesel: "Diesel",
    electrique: "Electric",
    electric: "Electric",
    hybride: "Hybrid",
    hybrid: "Hybrid",
  },
  body_style: {
    berline: "SEDAN",
    sedan: "SEDAN",
    familiale: "ESTATE",
    estate: "ESTATE",
    fourgonnette: "VAN",
    van: "VAN",
    cabriolet: "CONVERTIBLE",
    convertible: "CONVERTIBLE",
    sport: "SPORTSCAR",
    sportscar: "SPORTSCAR",
    camionnette: "PICKUP",
    pickup: "PICKUP",
    "4x4-suv": "SUV",
    suv: "SUV",
    "a hayon": "HATCHBACK",
    hayon: "HATCHBACK",
    hatchback: "HATCHBACK",
  },
};

const toEnglishVehicleField = (
  value: string | undefined,
  field: VehicleFieldKey
): string | undefined => {
  if (!value) return value;

  const dictionary = vehicleFieldTranslations[field];
  return value
    .split(",")
    .map((segment) => segment.trim())
    .filter(Boolean)
    .map((segment) => dictionary[normalizeToken(segment)] ?? segment)
    .join(", ");
};

const toNumber = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) return value;

  const scalar = toStringScalar(value);
  if (!scalar) return undefined;

  const parsed = Number(scalar.replace(/\s+/g, "").replace(",", "."));
  return Number.isFinite(parsed) ? parsed : undefined;
};

const normalizeVehicleState = (value: unknown): string | undefined => {
  if (typeof value === "boolean") return value ? "new" : "used";

  const scalar = toStringScalar(value);
  if (!scalar) return undefined;

  const normalized = scalar.toLowerCase();
  if (["true", "1", "new", "neuf", "nouveau"].includes(normalized)) return "new";
  if (["false", "0", "used", "occasion"].includes(normalized)) return "used";
  return scalar;
};

const formatRangeNumber = (value: number): string =>
  Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/\.?0+$/, "");

const resolvePreferredPriceRange = (source: unknown): string | undefined => {
  const explicit = pickValue(source, ["preferred_price_range", "preferredPriceRange"]);
  if (Array.isArray(explicit)) {
    const [minRaw, maxRaw] = explicit;
    const min = toNumber(minRaw);
    const max = toNumber(maxRaw);
    if (typeof min === "number" || typeof max === "number") {
      return `[${typeof min === "number" ? formatRangeNumber(min) : ""},${
        typeof max === "number" ? formatRangeNumber(max) : ""
      }]`;
    }
  }

  const explicitString = toStringScalar(explicit);
  if (explicitString) return explicitString;

  const min = toNumber(pickValue(source, ["minPrice", "priceMin"]));
  const max = toNumber(pickValue(source, ["maxPrice", "priceMax"]));
  if (typeof min !== "number" && typeof max !== "number") return undefined;

  return `[${typeof min === "number" ? formatRangeNumber(min) : ""},${
    typeof max === "number" ? formatRangeNumber(max) : ""
  }]`;
};

const collectContentIds = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return Array.from(new Set(value.flatMap((item) => collectContentIds(item))));
  }

  const id = normalizeVehicleId(value as VehicleIdSource);
  return id ? [id] : [];
};

const resolveContentIdsFromSource = (source: unknown): string[] | undefined => {
  const rawIds = pickValue(source, [
    "content_ids",
    "contentIds",
    "vehicle_ids",
    "vehicleIds",
    "announcementIds",
    "ids",
    "content_id",
    "contentId",
    "vehicle_id",
    "vehicleId",
    "announcementId",
    "id",
  ]);
  const ids = collectContentIds(rawIds);
  return ids.length ? ids : undefined;
};

const buildVehicleFields = (source: unknown): MetaPayload => {
  const payload: MetaPayload = {};

  const make = toJoinedString(pickValue(source, ["make", "make.name", "makes", "marque", "marques"]));
  if (make) payload.make = make;

  const model = toJoinedString(pickValue(source, ["model", "model.name", "models", "modele", "modeles"]));
  if (model) payload.model = model;

  const year = toStringScalar(pickValue(source, ["year", "registrationYear", "fromRegYear"]));
  if (year) payload.year = year;

  const stateOfVehicle = normalizeVehicleState(
    pickValue(source, ["state_of_vehicle", "stateOfVehicle", "vehicleCondition", "isNew"])
  );
  if (stateOfVehicle) payload.state_of_vehicle = stateOfVehicle;

  const exteriorColor = toJoinedString(
    pickValue(source, ["exterior_color", "exteriorColor", "bodyColor", "body_color"])
  );
  if (exteriorColor) payload.exterior_color = exteriorColor;

  const transmission = toJoinedString(
    pickValue(source, ["transmissionEnglish", "transmissionsEnglish", "transmission", "transmissions"])
  );
  if (transmission) payload.transmission = toEnglishVehicleField(transmission, "transmission");

  const bodyStyle = toJoinedString(
    pickValue(source, [
      "bodyStyleEnglish",
      "bodyTypeEnglish",
      "categoriesEnglish",
      "categoryEnglish",
      "body_style",
      "bodyStyle",
      "bodyType",
      "categories",
      "category",
    ])
  );
  if (bodyStyle) payload.body_style = toEnglishVehicleField(bodyStyle, "body_style");

  const fuelType = toJoinedString(
    pickValue(source, ["fuelTypeEnglish", "fuelTypesEnglish", "fuel_type", "fuelType", "fuelTypes"])
  );
  if (fuelType) payload.fuel_type = toEnglishVehicleField(fuelType, "fuel_type");

  const drivetrain = toStringScalar(pickValue(source, ["drivetrain", "driveTrain"]));
  if (drivetrain) payload.drivetrain = drivetrain;

  const postalCode = toStringScalar(pickValue(source, ["postal_code", "postalCode", "zipCode", "zip"]));
  if (postalCode) payload.postal_code = postalCode;

  payload.country = toStringScalar(pickValue(source, ["country", "countryName"])) ?? "Tunisia";

  const price = toNumber(pickValue(source, ["price"]));
  if (typeof price === "number") payload.price = price;

  const preferredPriceRange = resolvePreferredPriceRange(source);
  if (preferredPriceRange) payload.preferred_price_range = preferredPriceRange;

  const currency =
    toStringScalar(pickValue(source, ["currency"])) ??
    (typeof price === "number" || preferredPriceRange ? "TND" : undefined);
  if (currency) payload.currency = currency;

  return payload;
};

const buildVehicleFieldSource = (
  primary?: MetaPayload,
  extra?: MetaPayload
): MetaPayload => {
  const nestedVehicle = isPlainObject(extra?.vehicle) ? (extra?.vehicle as MetaPayload) : {};
  return {
    ...nestedVehicle,
    ...(primary || {}),
    ...(extra || {}),
  };
};

const resolveContentIdsFromVehicleId = (vehicleId: string): string[] | undefined => {
  const normalizedVehicleId = normalizeVehicleId(vehicleId);
  const contentIds = toMetaContentIds(normalizedVehicleId);
  return contentIds?.length ? contentIds : undefined;
};

const buildVehicleEventPayload = ({
  source,
  extra,
  contentIds,
  additional,
}: {
  source?: MetaPayload;
  extra?: MetaPayload;
  contentIds?: string[];
  additional?: MetaPayload;
}): MetaPayload => {
  const payload: MetaPayload = {
    ...stripUnsafeKeys(extra),
    ...(additional || {}),
    ...buildVehicleFields(buildVehicleFieldSource(source, extra)),
    content_type: "vehicle",
  };

  if (contentIds?.length) {
    payload.content_ids = contentIds;
  }

  return payload;
};

export const useMetaPixel = () => {
  const route = useRoute();
  const runtimeConfig = useRuntimeConfig();

  const isDebug = () =>
    runtimeConfig.public.metaDebug === true ||
    String(runtimeConfig.public.metaDebug).toLowerCase() === "true";

  const trackPageView = () => {
    if (!process.client) return;
    trackMetaStandardEvent("PageView", undefined, isDebug());
  };

  const trackViewContent = (vehicleId: string, extra?: MetaPayload) => {
    if (!process.client) return;
    const contentIds = resolveContentIdsFromVehicleId(vehicleId);
    if (!contentIds?.length) return;

    const dedupeKey = `${route.path}:${contentIds[0]}`;
    if (viewContentDedupe.has(dedupeKey)) return;

    const payload = buildVehicleEventPayload({ extra, contentIds });

    const sent = trackMetaStandardEvent("ViewContent", payload, isDebug());
    if (sent) {
      viewContentDedupe.add(dedupeKey);
    }
  };

  const trackSearch = (
    filters: Record<string, unknown>,
    resultCount?: number,
    results?: unknown[]
  ) => {
    if (!process.client) return;
    const normalizedFilters = normalizeFilters(filters || {});
    let contentIds = resolveContentIdsFromSource(normalizedFilters);
    if (!contentIds?.length && Array.isArray(results)) {
      const resolvedFromResults = Array.from(
        new Set(results.flatMap((item) => collectContentIds(item)))
      ).slice(0, 50);
      if (resolvedFromResults.length) {
        contentIds = resolvedFromResults;
      }
    }
    const additional: MetaPayload = {
      search_string: buildSearchString(normalizedFilters),
      content_category: "vehicle",
      ...normalizedFilters,
    };

    if (typeof resultCount === "number" && Number.isFinite(resultCount)) {
      additional.result_count = resultCount;
    }

    // Search payload may come from URL/store filters, not only API vehicle objects.
    const payload = buildVehicleEventPayload({
      source: normalizedFilters,
      contentIds,
      additional,
    });

    trackMetaStandardEvent("Search", payload, isDebug());
  };

  const trackAddToWishlist = (vehicleId: string, extra?: MetaPayload) => {
    if (!process.client) return;
    const contentIds = resolveContentIdsFromVehicleId(vehicleId);
    if (!contentIds?.length) return;

    const payload = buildVehicleEventPayload({ extra, contentIds });

    trackMetaStandardEvent("AddToWishlist", payload, isDebug());
  };

  const trackLead = (
    vehicleId: string,
    leadType: LeadType,
    extra?: MetaPayload
  ) => {
    if (!process.client) return;
    const contentIds = resolveContentIdsFromVehicleId(vehicleId);
    if (!contentIds?.length) return;

    const payload = buildVehicleEventPayload({
      extra,
      contentIds,
      additional: { lead_type: leadType },
    });

    trackMetaStandardEvent("Lead", payload, isDebug());
  };

  const trackCompleteRegistration = (extra?: Record<string, unknown>) => {
    if (!process.client) return;
    trackMetaStandardEvent("CompleteRegistration", extra || {}, isDebug());
  };
  
  return {
    trackPageView,
    trackViewContent,
    trackSearch,
    trackAddToWishlist,
    trackLead,
    trackCompleteRegistration,
  };
};
